import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'next/router';
import { debounce } from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  CircularProgress,
  FormControl,
  Typography,
  MenuItem,
  Select,
} from '@material-ui/core';
import PageBase from '../../../components/layout/PageBase';
import Listings from '../../../components/utility/Listings';
import LogoLoader from '../../../components/utility/LogoLoader';
import ToolsHeader from '../../../components/pages/tools/header/index';
import NoResults from '../../../components/utility/NoResults';
import LocationForm from '../../../components/utility/LocationForm/index';
import {
  fetchInvestmentProps,
  LISTINGS_FETCH_LIMIT,
} from '../../../actions/listing';
import { buttonStyles } from '../../../styles/base';
import track from '../../../lib/track';
import * as types from '../../../actions/actionTypes';
import combineStyles from '../../../styles/combineStyles';
import BackButton from '../../../components/utility/BackButton';
import style from '../../../styles/sass/pages/Tools.module.scss';

const freshFindsHeadline = 'Enter a location and minimum cap rate';
const freshFindsToolTip = `The Fresh Finds tool shows you high-value investment properties, based on their capitalization, or “cap rate.” A property’s cap rate is the yearly return that you will receive in rent. Cap rate is calculated by dividing a property’s Net Operating Income (taxes, insurance, operating expenses) by the home price.`;

export interface Props {
  classes: any;
  dispatch: any;
  investmentPropsCity: string | undefined;
  fetchInvestmentProps: Function;
  investmentPropsFetched: boolean;
  investmentPropsState: string;
  listings: any[];
  loading: boolean;
  minCapRate: number;
  router: any;
  token?: string;
}

interface State {
  viewedOnce: boolean;
  minCapRate: number | undefined;
}

const styles = {
  capRateContainer: {
    display: 'flex',
  },
  formLabel: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1,
    marginTop: 3,
    padding: 0,
    textAlign: 'left',
  },
  left: {
    textAlign: 'left',
  },
};

export class FreshFinds extends React.Component<Props, State> {
  capRateOptions = [3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20, 25];

  constructor(props: Props) {
    super(props);
    this.state = {
      minCapRate: this.props.minCapRate || 8,
      viewedOnce: false,
    };
    this.loadMoreHandler = debounce(this.loadMoreHandler, 300);
  }

  componentDidMount() {
    track.toolUsed('Fresh Finds');
  }

  setViewed = () => {
    this.setState({
      viewedOnce: true,
    });
  };

  openSignup = () => {
    this.props.dispatch({
      postAuthPath: this.props.router.pathname,
      signupSubtext: `Sign up for full access to Torii's high-value investment properties as soon as they hit the market.`,
      type: types.OpenSignupDialog,
    });
  };

  loadMoreHandler = (
    offset: number,
    state: string,
    minCapRate: number = 8,
    reset?: boolean,
    city?: string,
  ) => {
    if (!this.props.token && this.state.viewedOnce) {
      this.openSignup();
    } else {
      !this.state.viewedOnce && this.setViewed();
      track.toolUsed('Fresh Finds', { location: state, minCapRate });
      const numberToFetch = this.props.token ? 18 : 3;
      this.props.fetchInvestmentProps(
        offset,
        numberToFetch,
        state,
        minCapRate,
        reset,
        city,
      );
    }
  };

  setCapRate = (minCapRate: number, submitForm: boolean) => {
    this.setState({ minCapRate }, () => {
      if (submitForm) {
        this.handleFormSubmit(
          this.props.investmentPropsState,
          true,
          this.props.investmentPropsCity,
        );
      }
    });
  };

  handleCapRate = (capRate: number) => {
    if (!this.props.investmentPropsFetched) {
      this.setCapRate(capRate, false);
    } else {
      this.setCapRate(capRate, true);
    }
  };

  handleFormSubmit = (state: string, reset: boolean, city?: string) => {
    this.loadMoreHandler(0, state, this.state.minCapRate, reset, city);
  };

  loadingContent = () => {
    return <LogoLoader />;
  };

  renderLoadButton = (buttonLoader: boolean) => {
    const {
      classes,
      investmentPropsCity,
      investmentPropsState,
      listings,
      loading,
      minCapRate,
    } = this.props;
    // Note that there's a bug where we're fetching the same listing twice.
    // This is a band-aid for that
    let offset = listings.length;
    if (listings.length % LISTINGS_FETCH_LIMIT === 1) {
      offset += 1;
    }
    return (
      <Button
        className={classes.buttonLargePink}
        color="secondary"
        disabled={loading || buttonLoader}
        onClick={() =>
          this.loadMoreHandler(
            offset,
            investmentPropsState,
            minCapRate,
            false,
            investmentPropsCity,
          )
        }
        size="large"
        variant="contained"
        style={{ margin: 'auto', marginTop: '50px' }}
      >
        {buttonLoader ? (
          <div style={{ marginTop: 5 }}>
            <CircularProgress
              color="inherit"
              size={40}
              thickness={2}
              variant="indeterminate"
            />
          </div>
        ) : (
          'LOAD MORE'
        )}
      </Button>
    );
  };

  loadedContent = () => {
    const { listings } = this.props;
    return (
      <div className={`col-xs-12 ${style.toolContent} tool-content`}>
        {listings.length > 0 ? (
          <div className={style.toolInnerContent}>
            <Listings
              allowFavorite
              listings={listings}
              narrow={false}
              showDelete={false}
              key={listings.length}
              useLoadButton
              customLoadButton={this.renderLoadButton}
            />
          </div>
        ) : (
          <div className={style.toolEmptyContainer}>
            <NoResults emptyText="Looks like there aren't any listings that match your search. Try adjusting your minimum cap rate and try again." />
          </div>
        )}
      </div>
    );
  };

  render() {
    const { classes, investmentPropsFetched, loading, router } = this.props;
    return (
      <PageBase title="Investment properties for sale | Torii Fresh Finds">
        <BackButton goBack={() => router.push('/tools')} />
        <div
          className={`${style.toolContainer} tool-container ${
            style.freshFinds
          } col center-xs ${investmentPropsFetched ? '' : style.loading}`}
        >
          <ToolsHeader
            title="Fresh Finds"
            slug="fresh-finds"
            subTitle="Discover high-value investment properties"
          />

          <LocationForm
            initialCity={this.props.investmentPropsCity}
            initialState={this.props.investmentPropsState}
            onSubmit={(state: string, city: string) => {
              this.handleFormSubmit(state, true, city);
            }}
            showCard={!this.props.investmentPropsFetched}
            toolTipText={freshFindsToolTip}
            toolsHeadline={freshFindsHeadline}
          >
            <div className={style.capRateInputContainer}>
              <Typography className={classes.formLabel}>
                Min. Cap Rate
              </Typography>
              <FormControl className={classes.capRateContainer}>
                <Select
                  className={classes.left}
                  inputProps={{
                    id: 'minSf',
                    name: 'minSf',
                  }}
                  MenuProps={{
                    anchorOrigin: {
                      horizontal: 'left',
                      vertical: 'bottom',
                    },
                    getContentAnchorEl: null,
                  }}
                  value={this.state.minCapRate}
                  onChange={(event: any) => {
                    this.handleCapRate(event.target.value);
                  }}
                >
                  {this.capRateOptions.map((capRate: number, index: number) => {
                    return (
                      <MenuItem
                        key={index}
                        value={capRate}
                        className={classes.left}
                      >
                        {capRate}%
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </div>
          </LocationForm>

          {loading ? (
            <LogoLoader />
          ) : (
            investmentPropsFetched && this.loadedContent()
          )}
        </div>
      </PageBase>
    );
  }
}

const mapStateToProps = (state: any) => ({
  investmentPropsCity: state.listings.investmentPropsCity,
  investmentPropsFetched: state.listings.investmentPropsFetched,
  investmentPropsState: state.listings.investmentPropsState,
  listings: state.listings.investmentProps,
  loading: state.listings.investmentPropsLoading,
  minCapRate: state.listings.investmentPropsMinCapRate,
  token: state.auth.token,
});

const mapDispatchToProps = (dispatch: any) => ({
  dispatch,
  fetchInvestmentProps(
    offset: number,
    limit: number,
    state: string,
    minCapRate: number,
    reset: boolean,
    city: string,
  ) {
    dispatch(
      fetchInvestmentProps(offset, limit, state, minCapRate, reset, city),
    );
  },
});

const combinedStyles = combineStyles(buttonStyles, styles);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(withStyles(combinedStyles)(FreshFinds)));
